import { Slider, SliderChangeEvent, SliderLabel } from "@progress/kendo-react-inputs";
import { Card, CardBody, CardHeader, CardTitle, StackLayout } from "@progress/kendo-react-layout";
import { useCallback, useContext, useEffect, useState } from "react";
import { useLocalization } from "@progress/kendo-react-intl";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { locales } from "../resources/locales";
import { AppContext } from "../AppContext";
import { AlarmsTile } from "../components/dashboard/AlarmsTile";
import { BeaconsTileCounter } from "../components/dashboard/BeaconsTileCounter";
import { Devices } from "../components/dashboard/Devices";
import { DevicesBatteryStatus } from "../components/dashboard/DevicesBatteryStatus";
import { RegularTag } from "../components/dashboard/RegularTag";
import packageJson from '../../package.json';
import axios from "axios";
import { AccessToken } from "../config/AccessToken";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

/**
 * @description
 * This component is used to display the settings of the app.
 * The user can toggle the visibility of the widgets and change the refresh time of the API.
 * The user can also change the regular tag and the battery percentage for the unloaded tags.
 * The user can also reset the layout of the widgets to its initial state.
 * The user can also change the language of the app.
 * @param {any} props The props passed to the component.
 * @returns {JSX.Element} The component to be rendered.
 * @example
 * import { Settings } from '../components/Settings';
 * 
 * <Settings />
 */
export const Settings = (props: any) => {

    const parseStoredNumber = (rawValue: string | null, fallback: number): number => {
        if (rawValue == null || rawValue === '') {
            return fallback;
        }

        try {
            const parsed = JSON.parse(rawValue);
            const normalized = typeof parsed === 'number' ? parsed : Number(parsed);
            return Number.isFinite(normalized) ? normalized : fallback;
        } catch {
            const normalized = Number(rawValue);
            return Number.isFinite(normalized) ? normalized : fallback;
        }
    };

    const localizationService = useLocalization();

    const { localeId, onLanguageChange } = useContext(AppContext);
    const currentLanguage = locales.find(item => item.localeId === localeId);
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const [instance, accessTokenRequest] = AccessToken();

    const userEmail = localStorage.getItem('email');
    const languageId = localStorage.getItem('languageId');
    const accountId = localStorage.getItem('accountId');

    const defaultValueRegularTag = [0, 6, 12, 18, 24, 30, 36, 42, 48];
    const [valueRegularTag, setValueRegularTag] = useState(5);

    const [clientConfig, setClientConfig] = useState<any>([]);


    let initialTiles: any = [
        {
            id: 0,
            defaultPosition: { col: 1, colSpan: 3, rowSpan: 1, order: 1 },
            header: localizationService.toLanguageString('dashboard.widget.devices.title', 'NOT_FOUND'),
            body:
                <Devices
                //rowSpan={data[0]?.['rowSpan']}
                //colSpan={data[0]?.['colSpan']}
                />,
            //resizable: false,
            visible: true
        },
        {
            id: 2,
            defaultPosition: { col: 1, colSpan: 1, rowSpan: 1, order: 2 },
            header: localizationService.toLanguageString('dashboard.widget.alarms_counter', 'NOT_FOUND'),
            body:
                <AlarmsTile />,
            //resizable: false,
            visible: true
        },
        {
            id: 5,
            defaultPosition: { col: 2, colSpan: 1, rowSpan: 1, order: 3 },
            header: localizationService.toLanguageString('beacons.title', 'NOT_FOUND'),
            body:
                <BeaconsTileCounter />,
            visible: true
        },
        {
            id: 3,
            defaultPosition: { col: 3, colSpan: 2, rowSpan: 1, order: 2 },
            header: localizationService.toLanguageString('dashboard.widget.regular_tag_2', 'NOT_FOUND'),
            body:
                <RegularTag />,
            //resizable: false,
            visible: true
        },
        {
            id: 4,
            defaultPosition: { col: 7, colSpan: 3, rowSpan: 2, order: 4 },
            header: localizationService.toLanguageString('dashboard.widget.devices.battery_status_2', 'NOT_FOUND'),
            body:
                <DevicesBatteryStatus
                    rowSpan={2}
                    colSpan={3}
                />,
            visible: true
            // resizable: (rowSpan && colSpan) == 1 || (rowSpan && colSpan) == 2 ? true : false,  
        },
    ];

    const stringify = (obj: any) => {
        let cache: any = [];
        let str = JSON.stringify(obj, function (key, value) {
            if (typeof value === "object" && value !== null) {
                if (cache.indexOf(value) !== -1) {
                    // Circular reference found, discard key
                    return;
                }
                // Store value in our collection
                cache.push(value);
            }
            return value;
        });
        cache = null; // reset the cache
        return str;
    }

    const [tiles, setTiles] = useState(() => {
        const savedState = localStorage.getItem(`tileState: ${userEmail}`);
        const parsedState = savedState ? JSON.parse(savedState) : [];

        return initialTiles.map((initialTile: any) => {
            const savedTile = parsedState.find((tile: any) => tile.id === initialTile.id);
            //if(initialTile.header.includes('Tag regolari') || initialTile.header.includes('Regular tag')) {
            //    removeCharsStartingWith(initialTile.header, '(')  
            //}
            return savedTile ? { ...initialTile, ...savedTile } : initialTile;
        });
    });

    const onLanguageChangeSystem: any = useCallback((event: any) => {
        localStorage.setItem('languageId', event.value.localeId)
    }, [languageId])

    useEffect(() => {
        saveTilesToLocalStorage(tiles);
    }, [tiles]);

    const saveTilesToLocalStorage = (tiles: any[]) => {
        const tileStateToSave = tiles.map(({ id, header, defaultPosition, visible }) => ({
            id,
            header,
            defaultPosition,
            visible,
        }));
        localStorage.setItem(`tileState: ${userEmail}`, JSON.stringify(tileStateToSave));
    };

    // refreshAPI
    const defaultValueRefreshAPI = [10, 20, 30, 40, 50, 60, 70];
    const [valueRefreshAPI, setValueRefreshAPI] = useState(30);

    const onChangeValueRefreshAPI = (event: SliderChangeEvent) => {
        setValueRefreshAPI(Math.round(event.value));
        localStorage.setItem('refreshAPI', stringify(Math.round(event.value)))
    };

    useEffect(() => {
        const refreshAPI = parseStoredNumber(localStorage.getItem('refreshAPI'), valueRefreshAPI)
        if (localStorage.getItem('refreshAPI') != null) {
            setValueRefreshAPI(refreshAPI)
        } else {
            localStorage.setItem('refreshAPI', stringify(Math.round(valueRefreshAPI)))
        }
    }, [])


    // regularTag
    const onChangeValueRegularTag = (event: SliderChangeEvent) => {
        props.regularTag(Math.round(event.value));
        const newValueRegularTag: any = Math.round(event.value)
        setValueRegularTag(newValueRegularTag);
        localStorage.setItem('regularTag', stringify(newValueRegularTag))
    };

    useEffect(() => {
        const regularTag = parseStoredNumber(localStorage.getItem('regularTag'), valueRegularTag)
        if (localStorage.getItem('regularTag') != null) {
            setValueRegularTag(regularTag)
        } else {
            localStorage.setItem('regularTag', stringify(Math.round(valueRegularTag)))
        }
    }, [])


    // batteryPercentage
    const defaultValueUnloadTag = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    const [valueUnloadTag, setValueUnloadTag] = useState(30);

    const onChangeValueUnloadTag = (event: SliderChangeEvent) => {
        props.batteryPercentage(Math.round(event.value));
        setValueUnloadTag(Math.round(event.value));
        localStorage.setItem('batteryPercentage', stringify(Math.round(event.value)))
    };

    useEffect(() => {
        const batteryPercentage = parseStoredNumber(localStorage.getItem('batteryPercentage'), valueUnloadTag)
        if (localStorage.getItem('batteryPercentage') != null) {
            setValueUnloadTag(Math.round(batteryPercentage))
        } else {
            localStorage.setItem('batteryPercentage', stringify(Math.round(valueUnloadTag)))
        }
    }, [])

    // statusDevice
    const defaultValueStatusDevice = [1, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    const [valueStatusDevice, setValueStatusDevice] = useState(75);

    const onChangeValueStatusDevice = (event: SliderChangeEvent) => {
        props.statusDevice(Math.round(event.value));
        setValueStatusDevice(Math.round(event.value));
        localStorage.setItem('statusDevice', stringify(Math.round(event.value)))
    };

    useEffect(() => {
        const statusDevice = parseStoredNumber(localStorage.getItem('statusDevice'), valueStatusDevice)
        if (localStorage.getItem('statusDevice') != null) {
            setValueStatusDevice(Math.round(statusDevice))
        } else {
            localStorage.setItem('statusDevice', stringify(Math.round(valueStatusDevice)))
        }
    }, [])

    const getClientConfig = async (accountId: any) => {
        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/settings/Client?clientId=${accountId}`, { headers })
                .then((res) => {
                    const data = res?.data?.data
                    setClientConfig(data);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    useEffect(() => {
        getClientConfig(accountId)
    }, [])

    const updateClientConfig = async (languageParams: any) => {

        const languageValue = languageParams.value.localeId
        // aggiorno il valore di Language
       
        const params = {
            settings: [{
                id: 'Language',
                group: 'VIE',
                description: 'Smart Locator Language',
                value: languageValue == 'it-IT' ? '1' : '0',
                clientId: Number(accountId)
            }]
        };

        console.log(params);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            try {
                await axios.put(`${SERVER_URL}/settings/Client`, params, { headers })
            } catch (error: any) {
                console.error("Errore:", error);
            }
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken)
            })
        }
    }

    const settingsPageStyle = {
        padding: '20px',
        background: 'linear-gradient(180deg, #f6fbfc 0%, #edf6f8 100%)'
    };

    const settingsHeaderStyle = {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '12px 14px',
        borderRadius: '12px',
        border: '1px solid #bfd8de',
        background: 'linear-gradient(180deg, #e4f3f7 0%, #d9edf2 100%)',
        boxShadow: '0 8px 22px rgba(7, 77, 90, 0.1)'
    };

    const settingsLabelStyle = {
        color: '#0e4b55',
        fontWeight: 700,
        letterSpacing: '0.01em',
        margin: 0
    };

    const settingsVersionStyle = {
        color: '#155e69',
        fontWeight: 700,
        fontSize: 'var(--fs-small)',
        background: '#eef8fb',
        border: '1px solid #b8d8df',
        borderRadius: '999px',
        padding: '4px 10px'
    };

    const stackStyle = {
        minHeight: '320px',
        paddingTop: 26,
        alignItems: 'stretch',
        flexWrap: 'nowrap' as const
    };

    const settingCardStyle = {
        minWidth: '0',
        width: 'auto',
        flex: '1 1 0',
        borderRadius: '14px',
        border: '1px solid #c3dce2',
        background: 'linear-gradient(180deg, #ffffff 0%, #f6fbfc 100%)',
        boxShadow: '0 12px 24px rgba(8, 69, 78, 0.1)',
        overflow: 'hidden' as const
    };

    const settingTitleStyle = {
        color: '#0b4a53',
        fontWeight: 700,
        letterSpacing: '0.015em'
    };

    const sliderValueStyle = {
        display: 'block',
        textAlign: 'center' as const,
        position: 'relative' as const,
        top: '10%',
        fontWeight: 700,
        fontSize: 'var(--fs-kpi)',
        color: '#0f6671',
        textShadow: '0 1px 0 rgba(255, 255, 255, 0.9)'
    };
   

    const settingsMotionCss = `
            @keyframes settingsFadeUp {
                from { opacity: 0; transform: translateY(8px); }
                to { opacity: 1; transform: translateY(0); }
            }

            .settings-surface {
                animation: settingsFadeUp 260ms ease-out both;
            }

            .settings-card {
                transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
            }

            .settings-card:hover {
                transform: translateY(-2px);
                border-color: #9bc8d1;
                box-shadow: 0 16px 28px rgba(8, 69, 78, 0.16);
            }

            .settings-card .k-card-header {
                background: linear-gradient(180deg, #eaf6f9 0%, #e0f1f5 100%);
                border-bottom: 1px solid #d0e6ec;
            }

            .settings-card .k-card-body {
                padding-top: 1rem;
            }

            .settings .button-language {
                min-width: 170px;
            }

            .settings .button-language .k-input {
                border-width: 2px;
                border-color: #a8ccd4;
                background: #f8fdff;
            }

            .settings-card .k-slider-items .k-label {
                color: #2f6671;
                font-weight: 600;
            }

            .settings-card .k-slider-items .k-label:hover {
                color: #0f6671;
            }

            @media (max-width: 1100px) {
                .settings-stack {
                    gap: 10px !important;
                }

                .settings-stack .settings-card {
                    min-width: 0 !important;
                    flex: 1 1 0 !important;
                }

                .settings-stack .settings-card .k-card-title {
                    font-size: 0.9rem;
                }

                .settings-stack .settings-card .k-card-body {
                    padding: 0.8rem;
                }

                .settings-stack .settings-card .k-slider-items .k-label {
                    font-size: 0.68rem;
                }
            }

            @media (max-width: 900px) {
                .settings-surface {
                    padding: 10px !important;
                }

                .settings {
                    padding: 10px !important;
                    gap: 8px !important;
                }

                .settings .language-chooser {
                    width: 100%;
                    justify-content: space-between;
                }

                .settings .button-language {
                    width: 58%;
                    min-width: 0;
                }

                .settings-stack {
                    gap: 8px !important;
                    padding-top: 14px !important;
                }

                .settings-stack .settings-card .k-card-title {
                    font-size: 0.82rem;
                }

                .settings-stack .settings-card .k-card-body {
                    padding: 0.65rem;
                }

                .settings-stack .settings-card .k-slider-items .k-label {
                    font-size: 0.62rem;
                }

                .settings-stack .settings-card .k-slider-items .k-label:nth-child(2n) {
                    display: none;
                }
            }

            @media (max-width: 640px) {
                .settings-stack {
                    gap: 6px !important;
                }

                .settings-stack .settings-card .k-card-title {
                    font-size: 0.76rem;
                    line-height: 1.1;
                }

                .settings-stack .settings-card .k-slider-items .k-label {
                    font-size: 0.56rem;
                }

                .settings-stack .settings-card .k-slider-items .k-label:nth-child(3n) {
                    display: none;
                }
            }

            .settings-card .k-slider .k-draghandle {
                background: #0f6671;
                border-color: #0d5a63;
            }

            .settings-card .k-slider .k-slider-track {
                background: #c9e3e8;
            }

            .settings-card .k-slider .k-slider-selection {
                background: linear-gradient(90deg, #1a8894 0%, #0f6671 100%);
            }

            @media (prefers-reduced-motion: reduce) {
                .settings-surface,
                .settings-card {
                    animation: none !important;
                    transition: none !important;
                }
            }
        `;

    return (
        <div className="k-d-flex-col settings-surface" style={settingsPageStyle}>
            <style>{settingsMotionCss}</style>

            <div className="settings" style={settingsHeaderStyle}>

                <div className="language-chooser" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={settingsLabelStyle}>{localizationService.toLanguageString('language', 'NOT_FOUND')}</span>
                    <DropDownList
                        className="button-language"
                        textField={'locale'}
                        dataItemKey={'localeId'}
                        data={locales}
                        value={currentLanguage}
                        onChange={(e: any) => {
                            onLanguageChange(e);
                            onLanguageChangeSystem(e);
                            updateClientConfig(e);
                        }}
                    />
                </div>

                <span style={settingsVersionStyle}>{localizationService.toLanguageString('version', 'NOT_FOUND')}: {packageJson.version}</span>
            </div>

            <StackLayout
                className="settings-stack"
                orientation={"horizontal"}
                gap={20}
                style={stackStyle}
            >

                {/* Refresh API */}
                <Card className="k-flex-20 settings-card" style={settingCardStyle}>
                    <CardHeader className="k-hbox">
                        <div>
                            <CardTitle style={settingTitleStyle}>{localizationService.toLanguageString('settings.refresh_api', 'NOT_FOUND')} (s)</CardTitle>
                        </div>
                    </CardHeader>
                    <CardBody>
                        <span style={sliderValueStyle}>
                            {valueRefreshAPI}s
                        </span>
                        <Slider
                            min={10}
                            max={70}
                            step={1}
                            value={valueRefreshAPI}
                            onChange={onChangeValueRefreshAPI}
                        >
                            {defaultValueRefreshAPI.map((value: any, id: any) => (
                                <SliderLabel
                                    title={value.toString() + ' ' + localizationService.toLanguageString('settings.seconds', 'NOT_FOUND')}
                                    key={id}
                                    position={value}
                                    onClick={() => {
                                        setValueRefreshAPI(value);
                                    }}
                                >
                                    {value.toString()}
                                </SliderLabel>
                            ))
                            }
                        </Slider>
                    </CardBody>
                </Card>

                {/* Regular Tag */}
                <Card className="k-flex-20 settings-card" style={settingCardStyle}>
                    <CardHeader className="k-hbox">
                        <div>
                            <CardTitle style={settingTitleStyle}>{localizationService.toLanguageString('settings.regular_tag_2', 'NOT_FOUND')} (h)</CardTitle>
                        </div>
                    </CardHeader>
                    <CardBody>
                        <span style={sliderValueStyle}>
                            {valueRegularTag}h
                        </span>
                        <Slider
                            min={0}
                            max={48}
                            step={1}
                            value={valueRegularTag}
                            onChange={onChangeValueRegularTag}
                        >
                            {defaultValueRegularTag.map((value: any, id: any) => (
                                <SliderLabel
                                    title={value.toString() + ' hours'}
                                    key={id}
                                    position={value}
                                    onClick={() => {
                                        setValueRegularTag(value);
                                    }}
                                >
                                    {value.toString()}
                                </SliderLabel>
                            ))
                            }
                        </Slider>
                    </CardBody>
                </Card>

                {/* Tag scariche */}
                <Card className="k-flex-20 settings-card" style={settingCardStyle}>
                    <CardHeader className="k-hbox">
                        <div>
                            <CardTitle style={settingTitleStyle}>{localizationService.toLanguageString('settings.unload_tag_2', 'NOT_FOUND')} (%)</CardTitle>
                        </div>
                    </CardHeader>
                    <CardBody>
                        <span style={sliderValueStyle}>
                            {valueUnloadTag}%
                        </span>
                        <Slider
                            min={10}
                            max={100}
                            step={10}
                            value={valueUnloadTag}
                            onChange={onChangeValueUnloadTag}
                        >
                            {defaultValueUnloadTag.map((value: any, id: any) => (
                                <SliderLabel
                                    title={value.toString() + ' %'}
                                    key={id}
                                    position={value}
                                    onClick={() => {
                                        setValueUnloadTag(value);
                                    }}
                                >
                                    {value.toString()}
                                </SliderLabel>
                            ))
                            }
                        </Slider>
                    </CardBody>
                </Card>

                {/* Status device */}
                <Card className="k-flex-20 settings-card" style={settingCardStyle}>
                    <CardHeader className="k-hbox">
                        <div>
                            <CardTitle style={settingTitleStyle}>{localizationService.toLanguageString('settings.status_device', 'NOT_FOUND')} (min)</CardTitle>
                        </div>
                    </CardHeader>
                    <CardBody>
                        <span style={sliderValueStyle}>
                            {valueStatusDevice}min
                        </span>
                        <Slider
                            min={1}
                            max={100}
                            step={10}
                            value={valueStatusDevice}
                            onChange={onChangeValueStatusDevice}
                        >
                            {defaultValueStatusDevice.map((value: any, id: any) => (
                                <SliderLabel
                                    title={value.toString() + ' min'}
                                    key={id}
                                    position={value}
                                    onClick={() => {
                                        setValueStatusDevice(value);
                                    }}
                                >
                                    {value.toString()}
                                </SliderLabel>
                            ))
                            }
                        </Slider>
                    </CardBody>
                </Card>

            </StackLayout>

        </div>
    )
}
